import { createContext, useContext, Dispatch, SetStateAction } from "react";
import {
    Ingredient,
    CompanyFromIngSupplier,
    IngredientLoadState,
} from "src/api/client-api/ingredients/useGetOneIngredient";
import { IngredientReferenceUnified } from "src/api/client-api/ingredientReferenceUnified/types";
import { SsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { Post } from "src/api/client-api/post/postSchema";
import { PostList } from "src/api/client-api/post/types/postList.types";
import { OlapScores, OlapScoresWithPrevScores } from "src/api/client-api/olapScores/olapScoresSchema";

/**
 * NOTE — This file is intentionally separated from `ExistingIngredientContext.tsx`
 * (which exports the `IngredientContextProvider` component) to keep the module
 * compatible with Vite React Fast Refresh's "consistent components exports"
 * rule. Mixing a hook (`useIngredientContext`) with a component export in the
 * same module breaks HMR: a stale provider stays mounted while the new hook
 * references a brand-new context instance, causing `useContext` to return
 * `null` and the runtime check below to throw.
 *
 * See https://github.com/vitejs/vite-plugin-react-swc#consistent-components-exports
 */
export type IngredientContextInitialValues = {
    ingredient: Ingredient | undefined;
    ingredientLoadState: IngredientLoadState;
    refetchIngredient: () => Promise<unknown>;
    isReady: boolean;
    isRefetching: boolean;

    scores: OlapScoresWithPrevScores | undefined;
    areScoresRefetching: boolean;

    suppliersScores: OlapScores | undefined;
    areSuppliersScoresRefetching: boolean;

    ingredientReference: IngredientReferenceUnified | undefined;
    isIngredientReferenceReady: boolean;

    ssqVersion: SsqVersion | undefined;
    isSsqVersionReady: boolean;

    posts: PostList[] | undefined;

    activePost: Post | undefined;
    isActivePostRefetching: boolean;
    isActivePostLoading: boolean;

    /**
     * Surfaced from the lazy `GET /api/ingredients/:id/ssq-post` query: when the
     * backend `SsqPostCreateProvider` refuses to create a post (e.g. because the
     * supplier carries an IngredientReference with resolvable scores for the active
     * campaign), the error envelope flows up here so the questionnaire screen can
     * render a dedicated explanatory state instead of an empty form.
     */
    activePostError: unknown;

    answerableSupplierId: number | null;
    readableSupplierIds: number[];
    questionnaireSupplierId: number | null;
    canModifySSQ: boolean;
    showIngredientSuppliers: boolean;
    supplierCompaniesWithActiveCompanyFirst: CompanyFromIngSupplier[];
    activeCompanyId: number | null;
    setActiveCompanyId: Dispatch<SetStateAction<number | null>>;
};

export const IngredientContext = createContext<IngredientContextInitialValues | null>(null);

export const useIngredientContext = () => {
    const context = useContext(IngredientContext);

    if (context) {
        return context;
    }

    throw new Error(`useIngredientContext must be used within an IngredientContextProvider`);
};
